import type { Db } from "./client";
import { getUserId } from "./client";
import {
  locationCreateSchema,
  locationUpdateSchema,
  type LocationCreateInput,
  type LocationUpdateInput,
} from "./schemas";
import { ValidationError, NotFoundError, throwIfPgError } from "./errors";
import type { LocationRow, LocationInsert } from "@garden/types/db";

export async function createLocation(
  client: Db,
  input: LocationCreateInput
): Promise<LocationRow> {
  const parsed = locationCreateSchema.safeParse(input);
  if (!parsed.success) {
    throw new ValidationError("invalid location input", parsed.error);
  }
  const userId = await getUserId(client);

  const { data, error } = await client
    .from("location")
    .insert({ ...parsed.data, user_id: userId } as LocationInsert) // `as` cast sanctioned at Supabase boundary per spec §5 (unknown→Json for jsonb fields)
    .select("*")
    .single();
  throwIfPgError(error);
  return data as LocationRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function listLocations(
  client: Db,
  gardenId?: string,
): Promise<LocationRow[]> {
  let q = client.from("location").select("*");
  if (gardenId) q = q.eq("garden_id", gardenId);
  const { data, error } = await q.order("created_at", { ascending: false });
  throwIfPgError(error);
  return (data ?? []) as LocationRow[];
}

export async function getLocation(
  client: Db,
  id: string
): Promise<LocationRow> {
  const { data, error } = await client
    .from("location")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("location", id);
  return data as LocationRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function updateLocation(
  client: Db,
  id: string,
  patch: LocationUpdateInput
): Promise<LocationRow> {
  const parsed = locationUpdateSchema.safeParse(patch);
  if (!parsed.success) {
    throw new ValidationError("invalid location patch", parsed.error);
  }
  const { data, error } = await client
    .from("location")
    .update(parsed.data as Partial<Omit<LocationInsert, "user_id">>) // `as` cast sanctioned at Supabase boundary per spec §5 (unknown→Json for jsonb fields)
    .eq("id", id)
    .select("*")
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("location", id);
  return data as LocationRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function deleteLocation(
  client: Db,
  id: string
): Promise<void> {
  const { error } = await client.from("location").delete().eq("id", id);
  throwIfPgError(error);
}
